import React, { useEffect, useState } from "react";
import { Dropdown } from "@optimizely/axiom";
import classnames from "classnames";

import { RoleType } from "./RoleTypeSelection";
import { SearchInput } from "../SearchInput/SearchInput";

import { IOrganizationProductInstance, OrganizationProduct } from "../../../domain/OrganizationProduct";
import { Role } from "../../../domain/Role";
import {
    ADMINCENTER_PRODUCT_ID,
    ALL_INSTANCES_OPTION,
    CUSTOMER_DEFINED_INSTANCE_ROLE,
    CUSTOMER_DEFINED_PRODUCT_ROLE,
    SYSTEM_ROLE_TYPE,
    TOGGLE_ALL_INSTANCES_OPTION
} from "../../../constants/constants";

import styles from "./RoleForm.module.scss";

type RoleTypeInstanceSelectorProps = {
    selectedInstances: IOrganizationProductInstance[];
    selectedProduct: OrganizationProduct | null;
    setSelectedInstances: React.Dispatch<React.SetStateAction<IOrganizationProductInstance[]>>;
    editing: boolean;
    role: Role | undefined;
    selectedInstanceTarget: (IOrganizationProductInstance | undefined)[];
    roleType: RoleType | undefined;
    disabled?: boolean;
    isTeamRoleDropdown?: boolean;
    isTeamRoleSelected?: boolean;
};

export const RoleTypeInstanceSelector = ({
    selectedInstances,
    selectedProduct,
    setSelectedInstances,
    editing,
    role,
    selectedInstanceTarget,
    roleType,
    disabled = false,
    isTeamRoleDropdown = false,
    isTeamRoleSelected = false
}: RoleTypeInstanceSelectorProps) => {
    const [filteredInstances, setFilteredInstances] = useState(selectedProduct?.instances || []);
    const [instanceSearchText, setInstanceSearchText] = useState("");

    const isInstanceRole = roleType === CUSTOMER_DEFINED_INSTANCE_ROLE;
    const isAdminCenter = selectedProduct?.id === ADMINCENTER_PRODUCT_ID;
    const isExp = selectedProduct?.id === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;

    const setInstanceToAll = () => {
        setSelectedInstances(filteredInstances);
    };

    const appliesToAllInstances = ({ role }: { role?: Role }) => {
        return [CUSTOMER_DEFINED_PRODUCT_ROLE, SYSTEM_ROLE_TYPE].indexOf(role?.roleType || "") > -1;
    };

    const instanceSelectionsText = appliesToAllInstances({ role })
        ? ALL_INSTANCES_OPTION.nickname
        : selectedInstanceTarget?.map((i) => i?.nickname || i?.name).join(", ");

    // TODO: Handle searching when not all instances have been fetched?
    useEffect(() => {
        const filtered = (selectedProduct?.instances || []).filter((i) => {
            return (
                i.nickname?.toLocaleLowerCase().includes(instanceSearchText) ||
                i.name?.toLowerCase().includes(instanceSearchText)
            );
        });
        setFilteredInstances(filtered);
    }, [instanceSearchText, selectedProduct?.instances]);

    useEffect(() => {
        if (role && selectedProduct && editing) {
            if (appliesToAllInstances({ role })) {
                setSelectedInstances([ALL_INSTANCES_OPTION]);
            } else {
                const instances = selectedProduct.instances.filter((i) => (role.targetIds || []).indexOf(i.id) > -1);
                setSelectedInstances(instances);
            }
        } else {
            setFilteredInstances(selectedProduct?.instances || []);
            // reset instances when the product is changed.
            if ((isExp || isAdminCenter) && selectedProduct?.instances.length === 1) {
                setSelectedInstances(selectedProduct?.instances);
            } else {
                setSelectedInstances([]);
            }
        }
    }, [role, selectedProduct, isExp, isAdminCenter, editing, setSelectedInstances]);

    const toggleInstanceSelection = ({ instance }: { instance: IOrganizationProductInstance }) => {
        if (isExp || isTeamRoleDropdown) {
            setSelectedInstances([instance]);
        } else {
            const updatedInstances = [...selectedInstances];
            const existingIndex = updatedInstances.findIndex((i) => i.id === instance.id);
            const allIndex = updatedInstances.findIndex((i) => i.name === ALL_INSTANCES_OPTION.name);

            // remove the "All" selection if it is selected
            if (allIndex > -1) {
                updatedInstances.splice(allIndex, 1);
            }
            if (existingIndex > -1) {
                updatedInstances.splice(existingIndex, 1);
            } else {
                updatedInstances.push(instance);
            }

            setSelectedInstances(updatedInstances);
        }
    };

    return (
        <>
            <Dropdown
                arrowIcon="down"
                isDisabled={disabled || isAdminCenter || !selectedProduct || !isInstanceRole}
                fullWidth
                buttonContent={
                    isExp || isAdminCenter
                        ? selectedInstances.map((i) => i.name).join(", ") || "Select product instance..."
                        : isTeamRoleDropdown
                        ? !!instanceSelectionsText &&
                          ((!role && !disabled) || (!!role && isTeamRoleSelected && isTeamRoleDropdown))
                            ? instanceSelectionsText
                            : "Select Instance..."
                        : "Select Instances..."
                }
                className={styles["role-form__dropdown"]}
                shouldHideChildrenOnClick={isExp || isTeamRoleDropdown}
            >
                <Dropdown.Contents>
                    {(selectedProduct?.instances || []).length > 10 && (
                        <SearchInput
                            placeholder="Search product instances..."
                            type="text"
                            className="push"
                            value={instanceSearchText}
                            onChange={({ value }) => {
                                setInstanceSearchText(value?.toLowerCase());
                            }}
                        />
                    )}
                    {!instanceSearchText && !isExp && !isAdminCenter && !isTeamRoleDropdown && (
                        <Dropdown.ListItem key="ALL">
                            <Dropdown.BlockLink
                                onClick={setInstanceToAll}
                                isItemSelected={
                                    filteredInstances?.length === selectedInstances?.length ||
                                    !!selectedInstances.find((i) => i.name === ALL_INSTANCES_OPTION.name)
                                }
                            >
                                {TOGGLE_ALL_INSTANCES_OPTION.name}
                            </Dropdown.BlockLink>
                        </Dropdown.ListItem>
                    )}
                    {!filteredInstances.length && <div className="push">No Results</div>}

                    {filteredInstances.map((instance: IOrganizationProductInstance) => {
                        const { id, name, nickname } = instance;
                        const isSelected = selectedInstances.find((i) => i.id === id);
                        return (
                            <Dropdown.ListItem key={id}>
                                <Dropdown.BlockLink
                                    isItemSelected={!!isSelected}
                                    isMultiSelect={!isExp && !isTeamRoleDropdown}
                                    isLink={!isSelected}
                                    onClick={() => toggleInstanceSelection({ instance })}
                                >
                                    {nickname || name}
                                </Dropdown.BlockLink>
                            </Dropdown.ListItem>
                        );
                    })}
                </Dropdown.Contents>
            </Dropdown>
            {!isExp && !isTeamRoleDropdown && !disabled && !!selectedInstances?.length && (
                <span
                    title={instanceSelectionsText}
                    className={classnames("oui-label push--top", styles["role-form__instance-selections"])}
                    data-testid="instance-selections"
                >
                    {instanceSelectionsText}
                </span>
            )}
        </>
    );
};

RoleTypeInstanceSelector.displayName = "RoleTypeInstanceSelector";
